import { itemFrames } from './frames'
import type { TimelineItem } from './types'
import round from 'lodash/round'
import { pinnedParticipant } from '../timing/followers'
import type {
  MediaItemAny,
  TimelineItemAny,
  Transform,
} from '../types/renderConfigV2'

/**
 * One persisted v2 item → the hydrated item the layers read (#77). Pure, so
 * the browser can turn template items into editor items exactly as
 * `hydrateTimeline` does on the server, which re-exports it.
 */
function box(
  transform: Transform | undefined,
  fallback: { top: number; left: number; width: number; height: number },
) {
  if (!transform) return { ...fallback, opacity: 1, rotation: 0 }
  return {
    top: transform.y,
    left: transform.x,
    width: transform.width,
    height: transform.height,
    opacity: transform.opacity ?? 1,
    rotation: transform.rotation ?? 0,
  }
}

function cropOf(transform: Transform | undefined) {
  return {
    cropLeft: transform?.crop?.left ?? 0,
    cropTop: transform?.crop?.top ?? 0,
    cropRight: transform?.crop?.right ?? 0,
    cropBottom: transform?.crop?.bottom ?? 0,
  }
}

export function hydrateItem(
  item: TimelineItemAny,
  {
    width,
    height,
    assetId,
  }: { width: number; height: number; assetId: string | null },
): TimelineItem {
  const frames = itemFrames(item)
  const canvas = { top: 0, left: 0, width, height }
  const base = {
    id: item.id,
    from: frames.from,
    durationInFrames: frames.durationInFrames,
    isDraggingInTimeline: false,
  }
  const seconds = { from: item.from, duration: item.duration }
  // Theme fields the user set by hand (#547), only when there are any.
  const picked = item.picked?.length ? { picked: [...item.picked] } : {}
  // Generation 3 (#541, #566): the item a follower reads (`follows`: a scene
  // or a free media item), or the scene any other item was placed for, only
  // when it names one; the validator never lets a follower carry both.
  // Generation 2 names a track instead (`for`), kept as `forTrackId` until
  // the writers switch.
  const followed =
    'follows' in item && item.follows !== undefined
      ? item.follows
      : 'scene' in item
        ? item.scene
        : undefined
  const scene = followed !== undefined ? { sceneId: followed } : {}
  // A speaker pin (#566), only on a pinned speaker text or picture.
  const pin = pinnedParticipant(item)
  const participantId = pin ? { participantId: pin } : {}

  switch (item.type) {
    case 'scene':
      return {
        ...base,
        ...canvas,
        opacity: 1,
        type: 'scene',
        ...(item.source
          ? {
              assetId: assetId!,
              audioStartFromInSeconds: item.sourceStart ?? 0,
            }
          : {}),
        decibelAdjustment: toDecibels(item.volume),
        playbackRate: 1,
        audioFadeInDurationInSeconds: item.fade?.in ?? 0,
        audioFadeOutDurationInSeconds: item.fade?.out ?? 0,
        zealous: {
          ...(item.source ? { source: item.source } : {}),
          ...(item.sourceEnd !== undefined
            ? { sourceEnd: item.sourceEnd }
            : {}),
          ...(item.highlightId ? { highlightId: item.highlightId } : {}),
          ...(item.layout ? { layout: item.layout } : {}),
          seconds,
          ...picked,
        },
      }
    case 'view': {
      const { rotation, ...b } = box(item.transform, canvas)
      return {
        ...base,
        ...b,
        rotation,
        type: 'view',
        borderRadius: item.transform?.borderRadius ?? 0,
        ...cropOf(item.transform),
        fadeInDurationInSeconds: item.fade?.in ?? 0,
        fadeOutDurationInSeconds: item.fade?.out ?? 0,
        keepAspectRatio: true,
        ...(item.fit ? { fit: item.fit } : {}),
        ...(item.backdrop ? { backdrop: item.backdrop } : {}),
        zealous: { sceneId: item.scene, seconds, ...picked },
      }
    }
    case 'video': {
      const { rotation, ...b } = box(item.transform, canvas)
      return {
        ...base,
        ...b,
        rotation,
        type: 'video',
        borderRadius: item.transform?.borderRadius ?? 0,
        ...cropOf(item.transform),
        videoStartFromInSeconds: item.sourceStart,
        decibelAdjustment: toDecibels(item.volume),
        playbackRate: 1,
        audioFadeInDurationInSeconds: (item.audioFade ?? item.fade)?.in ?? 0,
        audioFadeOutDurationInSeconds: (item.audioFade ?? item.fade)?.out ?? 0,
        fadeInDurationInSeconds: item.fade?.in ?? 0,
        fadeOutDurationInSeconds: item.fade?.out ?? 0,
        assetId: assetId!,
        keepAspectRatio: true,
        ...(item.fit ? { fit: item.fit } : {}),
        ...(item.backdrop ? { backdrop: item.backdrop } : {}),
        zealous: {
          source: item.source,
          highlightId: item.highlightId,
          ...sceneEnd(item),
          seconds,
          ...picked,
          ...scene,
        },
      }
    }
    case 'audio':
      return {
        ...base,
        ...canvas,
        opacity: 1,
        type: 'audio',
        audioStartFromInSeconds: item.sourceStart,
        decibelAdjustment: toDecibels(item.volume),
        playbackRate: 1,
        audioFadeInDurationInSeconds: item.fade?.in ?? 0,
        audioFadeOutDurationInSeconds: item.fade?.out ?? 0,
        assetId: assetId!,
        zealous: {
          source: item.source,
          highlightId: item.highlightId,
          ...sceneEnd(item),
          seconds,
          ...picked,
          ...scene,
        },
      }
    case 'image': {
      const { rotation, ...b } = box(item.transform, canvas)
      return {
        ...base,
        ...b,
        rotation,
        type: 'image',
        borderRadius: item.transform?.borderRadius ?? 0,
        ...cropOf(item.transform),
        assetId: assetId!,
        keepAspectRatio: true,
        fadeInDurationInSeconds: item.fade?.in ?? 0,
        fadeOutDurationInSeconds: item.fade?.out ?? 0,
        zealous: { seconds, ...picked, ...scene },
      }
    }
    case 'text': {
      const lineHeightPx = item.style.fontSize * item.style.lineHeight
      const { rotation, ...b } = box(item.transform, {
        top: Math.round(height / 2 - lineHeightPx),
        left: Math.round(width * 0.06),
        width: Math.round(width * 0.88),
        height: Math.round(lineHeightPx * 2),
      })
      return {
        ...base,
        ...b,
        rotation,
        type: 'text',
        // A dynamic text has no words of its own (an older document may still
        // carry the host there).
        text: item.dynamic ? '' : item.text,
        color: item.style.color,
        align: item.style.align,
        fontFamily: item.style.fontFamily,
        fontStyle: {
          variant: item.style.fontStyle ?? 'normal',
          weight: String(item.style.fontWeight),
        },
        fontSize: item.style.fontSize,
        lineHeight: item.style.lineHeight,
        letterSpacing: item.style.letterSpacing,
        resizeOnEdit: false,
        direction: 'ltr',
        strokeWidth: item.style.strokeWidth ?? 0,
        strokeColor: item.style.strokeColor ?? '#000000',
        ...(item.style.maxLines ? { maxLines: item.style.maxLines } : {}),
        fadeInDurationInSeconds: item.fade?.in ?? 0,
        fadeOutDurationInSeconds: item.fade?.out ?? 0,
        background: item.style.background
          ? {
              color: item.style.background.color,
              horizontalPadding: item.style.background.padding,
              borderRadius: item.style.background.radius,
            }
          : null,
        zealous: {
          seconds,
          ...picked,
          ...scene,
          ...(item.dynamic
            ? {
                dynamic: {
                  field: item.dynamic.field,
                  ...('for' in item.dynamic
                    ? { forTrackId: item.dynamic.for }
                    : {}),
                  ...participantId,
                },
              }
            : {}),
        },
      }
    }
    case 'solid': {
      const { rotation, ...b } = box(item.transform, canvas)
      return {
        ...base,
        ...b,
        rotation,
        type: 'solid',
        borderRadius: item.transform?.borderRadius ?? 0,
        color: item.color,
        ...(item.stroke ? { stroke: item.stroke } : {}),
        keepAspectRatio: false,
        fadeInDurationInSeconds: item.fade?.in ?? 0,
        fadeOutDurationInSeconds: item.fade?.out ?? 0,
        zealous: { seconds, ...picked, ...scene },
      }
    }
    case 'captions': {
      const captionHeight = Math.round(
        item.style.linesPerPage * item.style.fontSize * item.style.lineHeight,
      )
      const { rotation, ...b } = box(item.transform, {
        top: Math.round(height - captionHeight - height * 0.12),
        left: Math.round(width * 0.06),
        width: Math.round(width * 0.88),
        height: captionHeight,
      })
      return {
        ...base,
        ...b,
        rotation,
        type: 'captions',
        ...('for' in item ? { forTrackId: item.for } : {}),
        style: item.style,
        zealous: { seconds, ...picked, ...scene },
      }
    }
    case 'component': {
      const { rotation, ...b } = box(item.transform, canvas)
      return {
        ...base,
        ...b,
        rotation,
        type: 'component',
        componentId: item.componentId,
        props: item.props,
        ...('for' in item && item.for !== undefined
          ? { forTrackId: item.for }
          : {}),
        fadeInDurationInSeconds: item.fade?.in ?? 0,
        fadeOutDurationInSeconds: item.fade?.out ?? 0,
        zealous: { seconds, ...picked, ...scene, ...participantId },
      } as TimelineItem
    }
  }
}

/**
 * A recording scene's window end; an earlier document's is
 * `sourceStart + duration`, filled here so a sync that shortens `duration`
 * can never shrink the window it was derived from.
 */
function sceneEnd(item: MediaItemAny): { sourceEnd?: number } {
  if (item.source.kind !== 'recording') return {}
  return {
    sourceEnd: item.sourceEnd ?? round(item.sourceStart + item.duration, 3),
  }
}

export function toDecibels(volume: number | undefined): number {
  if (volume === undefined || volume === 1) return 0
  if (volume <= 0) return -60
  return Math.round(20 * Math.log10(volume) * 100) / 100
}
